import { defineStore } from 'pinia'
import { getUsers, getUserById } from '@/api/v2/users'
import type { User } from '@/models/User'

interface UsersState {
  user_list: User[]
}

const userCache = new Map<string, User>()
const inFlightById = new Map<string, Promise<User>>()

/** Bumped by resetState(); answers of an older session are dropped. */
let listEpoch = 0

export const useUsersStore = defineStore('users', {
  state: (): UsersState => ({
    user_list: []
  }),

  actions: {
    resetState() {
      // A user list of the previous session still in flight must not land in the new one.
      listEpoch++
      this.user_list = []
      userCache.clear()
      inFlightById.clear()
    },

    async getUserList(): Promise<User[]> {
      const session = listEpoch
      const resp = await getUsers(null, null, null, false) // unscoped
      if (session !== listEpoch) return this.user_list
      this.user_list = resp.data.data
      userCache.clear()
      for (const user of this.user_list) {
        userCache.set(user.id, user)
      }
      return this.user_list
    },

    async getUserById(id: string): Promise<User> {
      if (!id) {
        throw new Error('User ID is required')
      }

      const cached = userCache.get(id)
      if (cached) {
        return cached
      }

      const inFlight = inFlightById.get(id)
      if (inFlight) {
        return inFlight
      }

      const promise = (async () => {
        try {
          const resp = await getUserById(id)
          const user = resp.data.data
          userCache.set(id, user)
          if (!this.user_list.some(u => u.id === user.id)) {
            this.user_list.push(user)
          }
          return user
        } finally {
          inFlightById.delete(id)
        }
      })()

      inFlightById.set(id, promise)
      return promise
    }
  }
})
